import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Bell,
  CircleHelp,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Upload,
  FileText,
  Download,
  MoreVertical,
  CheckCircle2,
  Building2,
  Paperclip,
  Sparkles,
  Plus,
  X,
} from "lucide-react";

import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";

import styles from "./Workspace.module.css";

const SummaryCard = ({ value, label }) => {
  return (
    <div className={styles.summaryCard}>
      <div className={styles.summaryIcon}>
        <Building2 size={17} />
      </div>

      <div className={styles.summaryContent}>
        <div className={styles.summaryValue}>{value}</div>
        <div className={styles.summaryLabel}>{label}</div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => {
  return (
    <div className={styles.detailRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
};

const MatchCheck = ({ children }) => {
  return (
    <div className={styles.matchCheck}>
      <CheckCircle2 size={15} />
      <span>{children}</span>
    </div>
  );
};

const PossibleMatch = ({
  title,
  subtitle,
  date,
  amount,
  percentage,
}) => {
  return (
    <div className={styles.possibleMatch}>
      <div>
        <div className={styles.possibleMatchTitle}>{title}</div>
        <div className={styles.possibleMatchSubtitle}>{subtitle}</div>
        <div className={styles.possibleMatchDate}>{date}</div>
      </div>

      <div className={styles.possibleMatchRight}>
        <strong>{amount}</strong>
        <span>{percentage}</span>
      </div>
    </div>
  );
};

const Workspace = () => {
      const navigate = useNavigate();
  
  return (
    <div className={styles.page}>
      <Sidebar />

      <div className={styles.mainArea}>
        <Header />

        <main className={styles.content}>
          {/* ================= BREADCRUMB ================= */}

          <div className={styles.breadcrumb}>
            <span>Reconciliation</span>
            <ChevronRight size={13} />
            <span>Workspace</span>
            <ChevronRight size={13} />
            <strong>RUN-08241</strong>
          </div>

          {/* ================= PAGE HEADER ================= */}

          <div className={styles.pageHeader}>
            <div>
              <div className={styles.titleRow}>
                <h1>RUN-08241–GTBank Main Account</h1>

                <span className={styles.statusBadge}>
                  In Progress
                </span>
              </div>

              <p>
                01 Aug 2026 - 14 Aug 2026 | Created by Adekunle O. on
                14 Aug 2026, 09:15 AM
              </p>
            </div>

            <div className={styles.headerActions}>
              <button className={styles.outlineButton}>
                <Upload size={14} />
                Export
              </button>

              <button className={styles.outlineButton}>
                Run Summary
              </button>

              <button className={styles.greenButton}                             
                onClick={() => navigate("/Reconcilation/NewReconciliationRun")}
              >
                Create New Rule
              </button>
            </div>
          </div>

          {/* ================= SUMMARY ================= */}

          <div className={styles.summaryGrid}>
            <SummaryCard
              value="18,420"
              label="Bank Transactions"
            />

            <SummaryCard
              value="18,210"
              label="Ledger Transactions"
            />

            <SummaryCard
              value="17,756(96.4%)"
              label="Matched"
            />

            <SummaryCard
              value="454 (2.5%)"
              label="Unmatched"
            />

            <SummaryCard
              value="210 (1.1%)"
              label="Exceptions"
            />

            <SummaryCard
              value="210 (1.1%)"
              label="Bank Transactions"
            />
          </div>

          {/* ================= MAIN WORKSPACE ================= */}

          <div className={styles.workspaceGrid}>
            {/* ================= BANK TRANSACTION ================= */}

            <section className={styles.transactionCard}>
              <div className={styles.transactionHeader}>
                <div className={styles.transactionIcon}>
                  <Building2 size={20} />
                </div>

                <div>
                  <h2>Bank Transaction</h2>
                  <span>GTBank</span>
                </div>
              </div>

              <div className={styles.cardDivider} />

              <div className={styles.amount}>
                ₦1,250,000.00
              </div>

              <div className={styles.credit}>Credit</div>

              <div className={styles.transactionInfo}>
                <p>14 August 2026</p>
                <p>TRF/2026/0814/827362</p>
                <p>ABC CONSTRUCTION LTD</p>
              </div>

              <div className={styles.balance}>
                <span>Balance After</span>
                <strong>₦15,250,000.00</strong>
              </div>

              <div className={styles.cardDivider} />

              <div className={styles.details}>
                <DetailRow
                  label="Value Date"
                  value="14 Aug 2026"
                />

                <DetailRow
                  label="Channel"
                  value="Internet Banking"
                />

                <DetailRow
                  label="Branch"
                  value="Victoria Island"
                />

                <DetailRow
                  label="Reference"
                  value="TRF/2026/0814/827362"
                />

                <DetailRow
                  label="Narration"
                  value="TRANSFER FROM ABC CONSTRUCTION LTD"
                />
              </div>

              <div className={styles.transactionPagination}>
                <button>
                  <ChevronLeft size={13} />
                  Previous
                </button>

                <span>21 of 210</span>

                <button>
                  Next
                  <ChevronRight size={13} />
                </button>
              </div>
            </section>

            {/* ================= MATCHING ================= */}

            <section className={styles.matchingCard}>
              <div className={styles.matchHeader}>
                <div className={styles.matchTitle}>
                  Best Match
                </div>

                <span className={styles.matchPercentage}>
                  96% Match
                </span>
              </div>

              <div className={styles.bestMatchBox}>
                <div className={styles.matchTop}>
                  <div>
                    <strong>ABC Construction Ltd</strong>
                    <span>Invoice INV-2026-00281</span>
                  </div>

                  <strong>₦1,250,000.00</strong>
                </div>

                <div className={styles.matchDate}>
                  14 Aug 2026
                </div>

                <div className={styles.matchReference}>
                  <span>Customer Receipt</span>
                  <strong>
                    4110 - Accounts Receivable
                  </strong>
                </div>

                <div className={styles.matchChecks}>
                  <MatchCheck>
                    Amount matches
                  </MatchCheck>

                  <MatchCheck>
                    Reference similar
                  </MatchCheck>

                  <MatchCheck>
                    Customer matches
                  </MatchCheck>

                  <MatchCheck>
                    Narration similar (92%)
                  </MatchCheck>

                  <MatchCheck>
                    Date within tolerance (0 days)
                  </MatchCheck>
                </div>
              </div>

              {/* ACTIONS */}

              <div className={styles.matchActions}>
                <button className={styles.confirmButton}>
                  <CheckCircle2 size={13} />
                  Confirm Match
                </button>

                <button className={styles.findButton}>
                  <Search size={13} />
                  Find Another Match
                </button>

                <button className={styles.moreButton}>
                  More Actions
                  <ChevronDown size={13} />
                </button>
              </div>

              <div className={styles.sectionDivider} />

              <h3 className={styles.possibleTitle}>
                Other Possible Matches
              </h3>

              <div className={styles.possibleMatches}>
                <PossibleMatch
                  title="ABC Construction Ltd"
                  subtitle="Invoice INV-2026-00281"
                  date="13 Aug 2026"
                  amount="₦1,250,000.00"
                  percentage="71%"
                />

                <PossibleMatch
                  title="ABC Construction Ltd"
                  subtitle="Payment on Account"
                  date="14 Aug 2026"
                  amount="₦1,250,000.00"
                  percentage="58%"
                />

                <PossibleMatch
                  title="ABC Construction Ltd"
                  subtitle="Invoice INV-2026-00275"
                  date="12 Aug 2026"
                  amount="₦1,250,000.00"
                  percentage="41%"
                />
              </div>
            </section>

            {/* ================= LEDGER TRANSACTION ================= */}

            <section className={styles.transactionCard}>
              <div className={styles.transactionHeader}>
                <div className={styles.transactionIcon}>
                  <Building2 size={20} />
                </div>

                <div>
                  <h2>Ledger Transaction</h2>
                  <span>General Ledger</span>
                </div>
              </div>

              <div className={styles.cardDivider} />

              <div className={styles.amount}>
                ₦1,250,000.00
              </div>

              <div className={styles.debit}>Debit</div>

              <div className={styles.transactionInfo}>
                <p>14 August 2026</p>
                <p>Invoice INV-2026-00281</p>
                <p>ABC CONSTRUCTION LTD</p>
              </div>

              <div className={styles.balance}>
                <span>Balance After</span>
                <strong>₦-</strong>
              </div>

              <div className={styles.cardDivider} />

              <div className={styles.details}>
                <h4>Additional Details</h4>

                <DetailRow
                  label="Value Date"
                  value="14 Aug 2026"
                />

                <DetailRow
                  label="Channel"
                  value="Internet Banking"
                />

                <DetailRow
                  label="Branch"
                  value="Victoria Island"
                />

                <DetailRow
                  label="Reference"
                  value="TRF/2026/0814/827362"
                />

                <DetailRow
                  label="Narration"
                  value="TRANSFER FROM ABC CONSTRUCTION LTD"
                />
              </div>
            </section>
          </div>

          {/* ================= BOTTOM AREA ================= */}

          <div className={styles.bottomGrid}>
            {/* TRANSACTION DETAILS */}

            <section className={styles.transactionDetails}>
              <div className={styles.bottomHeader}>
                <h2>Transaction Details</h2>

                <div className={styles.bottomIcons}>
                  <Paperclip size={15} />
                  <FileText size={15} />
                  <ChevronDown size={15} />
                </div>
              </div>

              <div className={styles.detailsColumns}>
                <div className={styles.detailColumn}>
                  <h3>Bank Details</h3>

                  <DetailRow
                    label="Bank"
                    value="GTBank"
                  />

                  <DetailRow
                    label="Account No."
                    value="**** **** **** 1234"
                  />

                  <DetailRow
                    label="Transaction Date"
                    value="14 Aug 2026"
                  />

                  <DetailRow
                    label="Value"
                    value="14 Aug 2026"
                  />

                  <DetailRow
                    label="Amount"
                    value="₦1,250,000.00"
                  />

                  <DetailRow
                    label="Type"
                    value="Credit"
                  />

                  <DetailRow
                    label="Currency"
                    value="NGN"
                  />

                  <DetailRow
                    label="Uploaded By"
                    value="Adekunle O."
                  />

                  <DetailRow
                    label="Upload Date"
                    value="14 Aug 2026 | 08:45 AM"
                  />
                </div>

                <div className={styles.verticalDivider} />

                <div className={styles.detailColumn}>
                  <h3>Matching Information</h3>

                  <DetailRow
                    label="Rule Matched"
                    value="Customer + Amount"
                  />

                  <DetailRow
                    label="Account No."
                    value="**** **** **** 1234"
                  />

                  <DetailRow
                    label="Transaction Date"
                    value="14 Aug 2026"
                  />

                  <DetailRow
                    label="Value"
                    value="14 Aug 2026"
                  />

                  <DetailRow
                    label="Amount"
                    value="₦1,250,000.00"
                  />

                  <DetailRow
                    label="Type"
                    value="Credit"
                  />

                  <DetailRow
                    label="Currency"
                    value="NGN"
                  />

                  <DetailRow
                    label="Uploaded By"
                    value="Adekunle O."
                  />

                  <DetailRow
                    label="Upload Date"
                    value="14 Aug 2026 | 08:45 AM"
                  />
                </div>
              </div>
            </section>

            {/* ================= RIGHT SIDE ================= */}

            <div className={styles.rightBottom}>
              <section className={styles.notesCard}>
                <div className={styles.notesHeader}>
                  <div>
                    <h3>Notes</h3>
                    <span>No notes added</span>
                  </div>

                  <button>
                    <Plus size={13} />
                    Add Note
                  </button>
                </div>

                <div className={styles.notesDivider} />

                <div className={styles.attachments}>
                  <h3>Attachments</h3>
                  <span>1 attached</span>

                  <div className={styles.attachment}>
                    <FileText size={16} />

                    <div>
                      <strong>payment_proof.pdf</strong>
                      <span>245 KB</span>
                    </div>

                    <Download size={14} />
                    <MoreVertical size={14} />
                  </div>
                </div>
              </section>

              <div className={styles.sideActions}>
                <button className={styles.skipButton}>
                  Skip
                </button>

                <button className={styles.exceptionButton}>
                  Create Exception
                </button>

                <button className={styles.autoMatchButton}>
                  <Sparkles size={14} />
                  Auto Match
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Workspace;